import { Box, Tab, Tabs } from "@mui/material";
import { useIsBelowDesktop } from "hooks/use-breakpoint";
import { useTranslation } from "react-i18next";
import type { Region } from "../types";

interface RegionTabsProps {
  regions: Region[];
  activeIndex: number;
  onRegionChange: (index: number) => void;
  /** Source image for the per-region thumbnails. When absent, tabs render
   *  text-only. */
  imageSrc?: string | null;
}

const RegionThumb = ({
  imageSrc,
  region,
  width,
  height,
}: {
  imageSrc: string;
  region: Region;
  width: number;
  height: number;
}) => {
  if (!region.width || !region.height) return null;

  const scaleX = 100 / region.width;
  const scaleY = 100 / region.height;

  return (
    <Box
      sx={{
        width,
        height,
        overflow: "hidden",
        position: "relative",
        borderRadius: "3px",
      }}
    >
      <Box
        component="img"
        src={imageSrc}
        alt=""
        sx={{
          position: "absolute",
          width: `${scaleX * 100}%`,
          height: `${scaleY * 100}%`,
          left: `${-region.x * scaleX}%`,
          top: `${-region.y * scaleY}%`,
          objectFit: "fill",
        }}
      />
    </Box>
  );
};

export const RegionTabs = ({
  regions,
  activeIndex,
  onRegionChange,
  imageSrc,
}: RegionTabsProps) => {
  const { t } = useTranslation();
  const isBelowDesktop = useIsBelowDesktop();
  const thumbWidth = isBelowDesktop ? 40 : 32;
  const thumbHeight = isBelowDesktop ? 28 : 22;

  if (regions.length <= 1) return null;

  return (
    <Tabs
      value={activeIndex}
      onChange={(_, value) => onRegionChange(value)}
      sx={{
        minHeight: 40,
        borderBottom: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
        "& .MuiTabs-indicator": {
          height: 2,
        },
      }}
      TabIndicatorProps={{
        style: { backgroundColor: regions[activeIndex]?.color },
      }}
    >
      {regions.map((region, index) => {
        const isActive = index === activeIndex;
        return (
          <Tab
            key={region.id}
            label={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {imageSrc && (
                  <Box
                    sx={{
                      border: `1.5px solid ${isActive ? region.color : "var(--alias-stroke-on-white-default, #d9d9d9)"}`,
                      borderRadius: "3px",
                      overflow: "hidden",
                      flexShrink: 0,
                      opacity: isActive ? 1 : 0.55,
                      transition: "opacity 0.15s",
                    }}
                  >
                    <RegionThumb
                      imageSrc={imageSrc}
                      region={region}
                      width={thumbWidth}
                      height={thumbHeight}
                    />
                  </Box>
                )}
                {t("ldp_explore_image_region_label", {
                  label: region.label,
                  defaultValue: "{{label}}",
                })}
              </Box>
            }
            sx={{
              textTransform: "none",
              minHeight: 40,
              padding: "9px 18px",
              fontSize: "var(--body-b3-12-regular-size, 12px)",
              fontFamily: 'var(--font-family, "AktivGrotesk")',
              fontWeight: isActive ? 500 : 400,
              color: isActive
                ? region.color
                : "var(--alias-text-on-white-secondary, #262626)",
              "&.Mui-selected": {
                color: region.color,
              },
              transition: "color 0.15s",
            }}
          />
        );
      })}
    </Tabs>
  );
};
